import React, {useEffect, useState} from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Row, Col, ListGroup, Image, Form, Button, Card } from 'react-bootstrap';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { addToCart, removeFromCart, saveVoucher, removeVoucherFromCart } from '../actions/cartActions';
import { validateVoucher, removeVoucher } from '../actions/voucherActions';
import { BOOK_PRODUCT_ID, PRINT_PRODUCT_ID, LANGUAGE_ADDON_PRODUCT_ID } from '../constants/productConfig';
import { usePricing } from '../hooks/usePricing';
import { getVatRateByCountry } from '../constants/vatConfig';

function CartScreen() {
    const { t } = useTranslation();
    const { id } = useParams(); // Get product id from URL
    const [searchParams] = useSearchParams();
    const qty = searchParams.get('qty') ? Number(searchParams.get('qty')) : 1;
    const { shippingPhysicalPrice } = usePricing();
    
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const cart = useSelector(state => state.cart);
    const { cartItems, error: cartError } = cart;
    const isLanguageAddonItem = (item) => (
        Number(item.product) === LANGUAGE_ADDON_PRODUCT_ID || item?.metadata?.lineItemType === 'language_addon'
    );
    const visibleCartItems = cartItems.filter(item => !isLanguageAddonItem(item));
    
    const userLogin = useSelector(state => state.userLogin);
    const { userInfo } = userLogin;
    
    const voucherValidate = useSelector(state => state.voucherValidate);
    const {
        loading: voucherLoading,
        success: voucherSuccess,
        voucher,
        discountAmount,
        shippingDiscountAmount,
        freeShippingApplied,
        error: voucherError
    } = voucherValidate;
    
    const [voucherCode, setVoucherCode] = useState(''); 

    const isPhysicalCartItem = (item) => (
        Number(item.product) === BOOK_PRODUCT_ID ||
        Number(item.product) === PRINT_PRODUCT_ID ||
        !item.is_digital
    );

    useEffect(() => {
        if (id) {
            dispatch(addToCart(id, qty));
        }
    }, [dispatch, id, qty]);
    
    // Save voucher to cart state when validated
    useEffect(() => {
        if (voucherSuccess && voucher) {
            dispatch(saveVoucher(
                voucher.code,
                Number(discountAmount || 0),
                Number(shippingDiscountAmount || 0),
                !!freeShippingApplied
            ));
        }
    }, [voucherSuccess, voucher, discountAmount, shippingDiscountAmount, freeShippingApplied, dispatch]);
    
    // Revalidate voucher when cart items change (e.g., item removed)
    useEffect(() => {
        if (voucherSuccess && voucher && cartItems.length > 0) {
            const subtotal = cartItems.reduce((acc, item) => acc + (parseFloat(item.price) * (item.qty || 1)), 0);
            // If subtotal is now less than minimum purchase amount, remove voucher
            if (voucher.min_purchase_amount && subtotal < voucher.min_purchase_amount) {
                dispatch(removeVoucher());
                dispatch(removeVoucherFromCart());
                setVoucherCode('');
            }
        }
    }, [cartItems.length, voucherSuccess, voucher, cartItems, dispatch]);



    const checkoutHandler = () => {
        // Check if all items are digital (no shipping needed)
        const allDigital = cartItems.length > 0 && cartItems.every(item => !isPhysicalCartItem(item));
        
        if (userInfo) {
            // Skip shipping for digital-only orders
            navigate(allDigital ? '/placeorder' : '/shipping');
        } else {
            // Redirect to login with appropriate next page
            const nextPage = allDigital ? 'placeorder' : 'shipping';
            navigate(`/login?redirect=${nextPage}`);
        }
    };

    const removeFromCartHandler = (cartItemId) => {
        dispatch(removeFromCart(cartItemId));
    };
    
    const applyVoucherHandler = () => {
        if (voucherCode.trim()) {
            const subtotal = cartItems.reduce((acc, item) => acc + (parseFloat(item.price) * (item.qty || 1)), 0);
            const hasPhysicalItems = cartItems.some(item => isPhysicalCartItem(item));
            const shippingTotal = hasPhysicalItems ? shippingPhysicalPrice : 0;
            dispatch(validateVoucher(voucherCode.trim(), subtotal, shippingTotal));
        }
    };
    
    const removeVoucherHandler = () => {
        dispatch(removeVoucher());  // Remove from voucher state
        dispatch(removeVoucherFromCart());  // Remove from cart state
        setVoucherCode('');
    };
    
    // Calculate price totals (ensure prices are numbers)
    const subtotal = cartItems.reduce((acc, item) => acc + (parseFloat(item.price) * (item.qty || 1)), 0);
    
    // Shipping: Free for digital products; physical shipping uses configured price
    const hasPhysicalItems = cartItems.some(item => isPhysicalCartItem(item));
    const shippingPrice = hasPhysicalItems ? shippingPhysicalPrice : 0;
    
    const hasAppliedVoucher = (voucherSuccess && voucher) || !!cart.voucherCode;
    const rawItemDiscount = voucherSuccess ? Number(discountAmount || 0) : Number(cart.discountAmount || 0);
    const rawShippingDiscount = voucherSuccess
        ? Number(shippingDiscountAmount || 0)
        : Number(cart.shippingDiscountAmount || 0);

    // Cap discounts so the final total never goes below zero by calculation artifacts.
    const itemDiscount = Math.min(rawItemDiscount, subtotal);
    const shippingDiscount = Math.min(rawShippingDiscount, shippingPrice);
    const totalDiscount = itemDiscount + shippingDiscount;

    // Tax: country-specific VAT on the post-discount total (included in price, shown separately)
    const preTaxTotal = subtotal + shippingPrice - totalDiscount;
    const vatRate = getVatRateByCountry(cart?.shippingAddress?.country);
    const taxIncluded = vatRate * preTaxTotal;
    
    // Total (subtotal + shipping - discount, minimum 0)
    const total = Math.max(0, preTaxTotal);

    return (
        <Row>
            
            <Col md={8}>
                <h1>{t('cart.title')}</h1>
                {cartError && <Message variant='danger'>{cartError}</Message>}
                {visibleCartItems.length === 0 ? (
                    <Message variant="info">
                        {t('cart.empty')} <Link to="/">{t('cart.goBack')}</Link>
                    </Message>
                ) : (
                    <ListGroup variant="flush">
                        {visibleCartItems.map(item => (
                            <React.Fragment key={item.cartItemId}>
                                <Card className='mb-3 d-md-none shadow-sm'>
                                    <Card.Body>
                                        <div className='d-flex gap-3 align-items-start'>
                                            <Image
                                                src={item.image}
                                                alt={item.name}
                                                fluid
                                                rounded
                                                style={{ width: '84px', height: '84px', objectFit: 'cover', flexShrink: 0 }}
                                            />
                                            <div className='flex-grow-1'>
                                                <div className='d-flex justify-content-between gap-2 align-items-start'>
                                                    <div>
                                                        <Link to={`/product/${item.product}`} className='fw-semibold text-decoration-none d-block'>
                                                            {item.name}
                                                        </Link>
                                                        <div className='text-muted small mt-1'>
                                                            {parseFloat(item.price).toFixed(2)} {t('common.dkk')}
                                                        </div>
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="light"
                                                        size="sm"
                                                        onClick={() => removeFromCartHandler(item.cartItemId)}
                                                    >
                                                        <i className="fas fa-trash"></i>
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>

                                <ListGroup.Item className='d-none d-md-block'>
                                    <Row className='align-items-center'>
                                        <Col md={2}>
                                            <Image src={item.image} alt={item.name} fluid rounded />    
                                        </Col>
                                        <Col md={6}>
                                            <Link to={`/product/${item.product}`}>
                                                {item.name}
                                            </Link>
                                        </Col>
                                        
                                        <Col md={3}>
                                            {parseFloat(item.price).toFixed(2)} {t('common.dkk')}
                                        </Col>

                                        <Col md={1}>
                                            <Button
                                                type="button"
                                                variant="light"
                                                onClick={() => removeFromCartHandler(item.cartItemId)}
                                            >
                                                <i className="fas fa-trash"></i>
                                            </Button>
                                        </Col>
                                    </Row>
                                </ListGroup.Item>
                            </React.Fragment>
                        ))}
                    </ListGroup>
                )}
            </Col>

            <Col md={4}>
                <Card>
                    <ListGroup variant="flush">
                        <ListGroup.Item>
                            <h2>
                                {t('cart.subtotal', { count: visibleCartItems.length })}
                            </h2>
                            <h3>
                                {subtotal.toFixed(2)} {t('common.dkk')}
                            </h3>
                        </ListGroup.Item>
                        
                        {/* Voucher Code Section */}
                        <ListGroup.Item>
                            <h5>{t('cart.discountCode')}</h5>
                            {!voucherSuccess ? (
                                <>
                                    <Form.Group className='mb-2'>
                                        <Form.Control
                                            type='text'
                                            placeholder={t('cart.voucherPlaceholder')}
                                            value={voucherCode}
                                            onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                                            disabled={voucherLoading}
                                        />
                                    </Form.Group>
                                    <Button
                                        type='button'
                                        variant='secondary'
                                        className='w-100'
                                        onClick={applyVoucherHandler}
                                        disabled={!voucherCode.trim() || voucherLoading || cartItems.length === 0}
                                    >
                                        {voucherLoading ? <Loader /> : t('cart.applyVoucher')}
                                    </Button>
                                    {voucherError && (
                                        <Message variant='danger' className='mt-2'>
                                            {voucherError}
                                        </Message>
                                    )}
                                </>
                            ) : (
                                <>
                                    <Message variant='success'>
                                        <div className='d-flex justify-content-between align-items-center'>
                                            <span>
                                                {t('cart.voucherApplied', { code: voucher.code })}
                                                <br />
                                                <small>{t('cart.saved', { amount: totalDiscount.toFixed(2) })}</small>
                                                {freeShippingApplied && (
                                                    <>
                                                        <br />
                                                        <small>Free shipping applied (-{Number(shippingDiscountAmount || 0).toFixed(2)} {t('common.dkk')})</small>
                                                    </>
                                                )}
                                            </span>
                                            <Button
                                                type='button'
                                                variant='outline-danger'
                                                size='sm'
                                                onClick={removeVoucherHandler}
                                            >
                                                {t('cart.remove')}
                                            </Button>
                                        </div>
                                    </Message>
                                </>
                            )}
                        </ListGroup.Item>
                        
                        {/* Order Summary */}
                        <ListGroup.Item>
                            <Row>
                                <Col>{t('cart.shipping')}</Col>
                                <Col>{shippingPrice.toFixed(2)} {t('common.dkk')}</Col>
                            </Row>
                            <Row className='mt-2'>
                                <Col>{t('placeOrder.tax')}</Col>
                                <Col>{taxIncluded.toFixed(2)} {t('common.dkk')}</Col>
                            </Row>
                            {hasAppliedVoucher && totalDiscount > 0 && (
                                <Row className='mt-2'>
                                    <Col>{t('cart.discount')}</Col>
                                    <Col className='text-success'>-{totalDiscount.toFixed(2)} {t('common.dkk')}</Col>
                                </Row>
                            )}
                            <Row className='mt-2'>
                                <Col><strong>{t('cart.totalLabel')}</strong></Col>
                                <Col><strong>{total.toFixed(2)} {t('common.dkk')}</strong></Col>
                            </Row>
                        </ListGroup.Item>

                        <ListGroup.Item>
                            <Button
                                type="button"
                                className="btn-block w-100"
                                disabled={cartItems.length === 0}
                                onClick={ checkoutHandler }
                            >
                                {total <= 0 ? t('placeOrder.submit') : t('cart.checkout')}
                            </Button>
                        </ListGroup.Item>

                    </ListGroup>
                </Card>
            </Col>
        </Row>
    );
}
export default CartScreen;